/**
 * Change the password — or add one to an account made with LinkedIn, Google,
 * Microsoft or Apple — a dialog from the Password row of Settings → Security
 * (design approved 26 Sep 2026).
 *
 * Renders the forms of `use-password-forms.ts`: the rule and the strength
 * meter under the new password, "sign out of other devices" (on by default
 * for a change), and the dialog closes once the password is saved.
 */
import { AlertTriangle, Loader2 } from '@tonnie/assets';
import { usePasswordRevealLabels, usePasswordStrengthLabels, useTranslation } from '@tonnie/locale';
import { meetsPasswordRule } from '@tonnie/schemas';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  PasswordInput,
  PasswordStrength,
} from '@tonnie/ui';
import type { ReactNode } from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

import { useChangePasswordForm, useSetPasswordForm } from './hooks/use-password-forms';

export function PasswordDialog({
  open,
  onOpenChange,
  hasPassword,
  email,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasPassword: boolean;
  /** Scores a password built from the address low. */
  email: string;
}) {
  const { t } = useTranslation();
  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        closeLabel={t('common.close')}
        data-testid="settings-password-dialog"
      >
        <DialogHeader className="gap-1">
          <DialogTitle className="text-lg font-semibold">
            {hasPassword
              ? t('auth.security.passwordDialog.changeTitle')
              : t('auth.security.passwordDialog.addTitle')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('auth.security.passwordDialog.description')}
          </DialogDescription>
        </DialogHeader>
        {open && hasPassword && <ChangeForm email={email} onDone={close} />}
        {open && !hasPassword && <SetForm email={email} onDone={close} />}
      </DialogContent>
    </Dialog>
  );
}

function ChangeForm({ email, onDone }: { email: string; onDone: () => void }) {
  const { t } = useTranslation();
  const reveal = usePasswordRevealLabels();
  const { form, onSubmit, error, isSubmitting } = useChangePasswordForm({ onSaved: onDone });
  const {
    register,
    watch,
    control,
    formState: { errors },
  } = form;

  return (
    <form onSubmit={onSubmit} noValidate data-testid="settings-password-change">
      <div className="flex flex-col gap-4 py-5">
        <FormError message={error} />
        <Field
          id="currentPassword"
          label={t('auth.security.passwordDialog.current')}
          error={errors.currentPassword?.message}
        >
          <PasswordInput
            id="currentPassword"
            revealLabels={reveal}
            autoComplete="current-password"
            autoFocus
            className="h-11 text-base"
            aria-invalid={!!errors.currentPassword}
            data-testid="settings-current-password-input"
            {...register('currentPassword')}
          />
        </Field>
        <NewPasswordFields
          email={email}
          reveal={reveal}
          password={watch('newPassword')}
          newField={register('newPassword')}
          confirmField={register('confirmPassword')}
          newError={errors.newPassword?.message}
          confirmError={errors.confirmPassword?.message}
        />
        <SignOutOthers control={control} name="signOutOfOtherSessions" />
      </div>
      <Footer
        submitting={isSubmitting}
        submitLabel={t('auth.security.passwordDialog.submitChange')}
        onCancel={onDone}
        testId="settings-update-password-btn"
      />
    </form>
  );
}

function SetForm({ email, onDone }: { email: string; onDone: () => void }) {
  const { t } = useTranslation();
  const reveal = usePasswordRevealLabels();
  const { form, onSubmit, error, isSubmitting } = useSetPasswordForm({ onSaved: onDone });
  const {
    register,
    watch,
    control,
    formState: { errors },
  } = form;

  return (
    <form onSubmit={onSubmit} noValidate data-testid="settings-password-set">
      <div className="flex flex-col gap-4 py-5">
        <FormError message={error} />
        <NewPasswordFields
          email={email}
          reveal={reveal}
          password={watch('newPassword')}
          newField={register('newPassword')}
          confirmField={register('confirmPassword')}
          newError={errors.newPassword?.message}
          confirmError={errors.confirmPassword?.message}
          autoFocus
        />
        <SignOutOthers control={control} name="signOutOfOtherSessions" />
      </div>
      <Footer
        submitting={isSubmitting}
        submitLabel={t('auth.security.passwordDialog.submitAdd')}
        onCancel={onDone}
        testId="settings-set-password-btn"
      />
    </form>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      {children}
      {error && <p className="text-sm text-destructive-accessible">{t(error)}</p>}
    </div>
  );
}

type Registered = ReturnType<ReturnType<typeof useSetPasswordForm>['form']['register']>;

function NewPasswordFields({
  email,
  reveal,
  password,
  newField,
  confirmField,
  newError,
  confirmError,
  autoFocus,
}: {
  email: string;
  reveal: { show: string; hide: string };
  password: string;
  newField: Registered;
  confirmField: Registered;
  newError?: string;
  confirmError?: string;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const strengthLabels = usePasswordStrengthLabels();
  return (
    <>
      <Field id="newPassword" label={t('auth.security.passwordDialog.new')} error={newError}>
        <PasswordInput
          id="newPassword"
          revealLabels={reveal}
          autoComplete="new-password"
          autoFocus={autoFocus}
          className="h-11 text-base"
          aria-invalid={!!newError}
          aria-describedby="newPassword-strength"
          data-testid="settings-new-password-input"
          {...newField}
        />
        <PasswordStrength
          id="newPassword-strength"
          className="mt-1"
          password={password}
          meetsRule={meetsPasswordRule(password)}
          userInputs={[email]}
          labels={strengthLabels}
        />
      </Field>
      <Field
        id="confirmPassword"
        label={t('auth.security.passwordDialog.confirm')}
        error={confirmError}
      >
        <PasswordInput
          id="confirmPassword"
          revealLabels={reveal}
          autoComplete="new-password"
          className="h-11 text-base"
          aria-invalid={!!confirmError}
          data-testid="settings-confirm-password-input"
          {...confirmField}
        />
      </Field>
    </>
  );
}

function SignOutOthers<T extends FieldValues>({
  control,
  name,
}: {
  control: Control<T>;
  name: FieldPath<T>;
}) {
  const { t } = useTranslation();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="flex items-start gap-2.5 rounded-[10px] bg-muted/60 px-3.5 py-3">
          <Checkbox
            id="signOutOfOtherSessions"
            className="mt-0.5"
            checked={Boolean(field.value)}
            onCheckedChange={(checked) => field.onChange(checked === true)}
            data-testid="settings-password-sign-out-others"
          />
          <span className="flex flex-col gap-0.5">
            <Label htmlFor="signOutOfOtherSessions" className="cursor-pointer text-sm font-medium">
              {t('auth.passwordCard.signOutOthers')}
            </Label>
            <span className="text-sm text-muted-foreground">
              {t('auth.passwordCard.signOutOthersHint')}
            </span>
          </span>
        </div>
      )}
    />
  );
}

function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="flex items-center gap-2 rounded-[10px] border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive-accessible"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      {message}
    </div>
  );
}

function Footer({
  submitting,
  submitLabel,
  onCancel,
  testId,
}: {
  submitting: boolean;
  submitLabel: string;
  onCancel: () => void;
  testId: string;
}) {
  const { t } = useTranslation();
  return (
    <DialogFooter className="gap-2 border-t border-border pt-4">
      <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={submitting} data-testid={testId}>
        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {submitLabel}
      </Button>
    </DialogFooter>
  );
}
